import { useSelector, useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import BreadCrum from "../../lib/BreadCrum";
import { USER_ROLES } from "../../utils/constant";
import { Box, Typography, Button, Theme, TextField, InputAdornment, IconButton, Menu, MenuItem, Dialog, DialogContent } from "@mui/material";
import theme from "../../themes/default";
import { DialogCloseIcon, PlusIcon, SearchIcon } from "../../assets/SvgIcons";
import { whiteSpaceOnlyRegex } from "../../utils/regex";
import CustomTable from "../investorOverview/common/customTable";
import OTPForm from "../../lib/formik/OtpForm";
import FamilyMemberTableBody, { familyMembersTableHeader, type FamilyMemberRow } from "./FamilyMemberTableBody";
import { getFamilyDashboard, addRemoveFamilyMember } from "../../redux-store/actions/investorProfile";
import { sendFamilyOtp, verifyFamilyOtp } from "../../redux-store/actions/investorProfile"
import { AddRemoveFamilyMemberRes, GetFamilyDashboardRes, SendFamilyOtpRes, VerifyFamilyOtpRes } from "../../redux-store/types/investorProfile";
import { isRMLikeRole } from "../../utils/utilityFunctions";
export type FamilyDetailsLocationState = {
    obj?: any;
};

export default function FamilyMember(): JSX.Element {
    const { role = "" } = useSelector((state: any) => state.auth);
    const { iwappRmId = "" } = useSelector((state: any) => state.id);
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation<FamilyDetailsLocationState | undefined>();
    const nav = (location.state ?? {}) as FamilyDetailsLocationState;
    const { obj = {} } = nav;
    const [searchText, setSearchText] = useState<string>('');
    const [familyMembers, setFamilyMembers] = useState<FamilyMemberRow[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isRemove, setIsRemove] = useState<boolean>(false);
    const [anchorEl, setanchorEl] = useState<HTMLElement | null>(null);
    const [menuSelectedMemberId, setMenuSelectedMemberId] = useState<string | null>(null);
    const [otpDialogOpen, setOtpDialogOpen] = useState<boolean>(false);
    const [otpDialogMemberId, setOtpDialogMemberId] = useState<string | null>(null);
    const [otpMessage, setOtpMessage] = useState<string>("");
    const [otpReference, setOtpReference] = useState<number>(0);
    const [removingMemberClientCode, setRemovingMemberClientCode] = useState<string>("");

    const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (whiteSpaceOnlyRegex.test(event.target.value)) return;
        setSearchText(event.target.value);
    }

    const handleClearSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
        setSearchText("")
        e.preventDefault()
    };

    const familySummary = [obj?.familyID, obj?.familyName].filter(Boolean).join(" - ");

    const filteredFamilyMembers = familyMembers.filter(member =>
        member.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        member.investorCode?.toLowerCase().includes(searchText.toLowerCase()) ||
        member.investorPan?.toLowerCase().includes(searchText.toLowerCase()) ||
        member.phoneNumber?.toLowerCase().includes(searchText.toLowerCase()) ||
        member.email?.toLowerCase().includes(searchText.toLowerCase())
    );

    useEffect(() => {
        const fetchFamilyDashboard = async () => {
            setIsLoading(true);
            try {
                const response = await dispatch(getFamilyDashboard({
                    rmId: iwappRmId,
                    familyId: obj.familyID,
                    level: "All",
                }) as unknown as Promise<GetFamilyDashboardRes>);

                if (response?.isSuccess && response?.result) {
                    const mappedData: FamilyMemberRow[] = response.result.map((member: any, index: number) => ({
                        id: member.customer,
                        name: member.customerName ?? "",
                        roleLabel: member.isFamilyHead === "Y" ? "Family Head" : " ",
                        investorCode: member.customer ?? "",
                        investorPan: member.panNumber ?? "",
                        phoneNumber: member.phoneNo ?? "",
                        email: member.emailId ?? "",
                        aum: member.marketValue ?? 0,
                    }));
                    setFamilyMembers(mappedData);
                }
            } catch (error) {
                console.error("Error fetching family dashboard:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchFamilyDashboard();
    }, []);

    const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>, memberId: string) => {
        setanchorEl(event.currentTarget);
        setMenuSelectedMemberId(memberId);
    };

    const handleCloseMenu = () => {
        setanchorEl(null);
        setMenuSelectedMemberId(null);
    };

    const handleSendOtp = async (memberId: string) => {
        const member = familyMembers.find((m) => m.id === memberId);
        setOtpDialogMemberId(memberId);
        setRemovingMemberClientCode(member?.investorCode ?? "");

        try {
            const response = await dispatch(sendFamilyOtp({
                clientCode: member?.investorCode ?? "",
                action: "REMOVE",
                isHeadOfFamily: false,
                familyName: obj?.familyName ?? "",
                hofName: obj?.familyHeadName ?? "",
            }) as unknown as Promise<SendFamilyOtpRes>);

            if (response?.isSuccess && response?.otpReference) {
                setOtpReference(response.otpReference);
                setOtpMessage(response.message);
                setOtpDialogOpen(true);
            } else {
                // Handle error
                console.error("Failed to send OTP:", response?.message);
            }
        } catch (error) {
            console.error("Error sending OTP:", error);
        }
    };
    const handleCloseOtpDialog = () => {
        setOtpDialogOpen(false);
        setOtpDialogMemberId(null);
        setOtpMessage("");
        setOtpReference(0);
        setRemovingMemberClientCode("");
    };

    const handleRemove = async () => {
        if (!menuSelectedMemberId) return;
        setIsRemove(true);
        await handleSendOtp(menuSelectedMemberId);
        setIsRemove(false);
        handleCloseMenu();
    };

    const handleSubmitOtp = async (otp: string) => {
        if (!otpDialogMemberId || !otpReference || !removingMemberClientCode) return;

        try {
            // First verify OTP
            const verifyResponse = await dispatch(verifyFamilyOtp({
                otp,
                otpReference,
            }) as unknown as Promise<VerifyFamilyOtpRes>);

            if (verifyResponse?.isSuccess) {
                // Then call addRemoveFamilyMember
                const removeResponse = await dispatch(addRemoveFamilyMember({
                    action: "REMOVE",
                    familyId: obj.familyID,
                    familyMembers: [{
                        otpReference: otpReference,
                        clientCode: removingMemberClientCode,
                        isHeadOfFamily: false,
                    }],
                }) as unknown as Promise<AddRemoveFamilyMemberRes>);

                if (removeResponse?.isSuccess) {
                    setFamilyMembers((prev) => prev.filter((m) => m.id !== otpDialogMemberId));
                    handleCloseOtpDialog();
                    history.push("/family/ViewFamily");
                } else {
                    // Handle error
                    console.error("Failed to remove member:", removeResponse?.message);
                }
            } else {
                // Handle OTP verification error
                console.error("OTP verification failed:", verifyResponse?.message);
            }
        } catch (error) {
            console.error("Error removing member:", error);
        }
    };

    const handleResendOtp = async () => {
        if (!otpDialogMemberId || !removingMemberClientCode) return;

        const member = familyMembers.find((m) => m.id === otpDialogMemberId);
        if (!member) return;

        try {
            const response = await dispatch(sendFamilyOtp({
                clientCode: removingMemberClientCode,
                action: "REMOVE",
                isHeadOfFamily: false,
                familyName: obj?.familyName ?? "",
                hofName: obj?.familyHeadName ?? "",
            }) as unknown as Promise<SendFamilyOtpRes>);

            if (response?.isSuccess && response?.otpReference) {
                setOtpReference(response.otpReference);
                setOtpMessage(response.message);
            } else {
                console.error("Failed to resend OTP:", response?.message);
            }
        } catch (error) {
            console.error("Error resending OTP:", error);
        }
    };

    const rmRouterPath = [
        {
            name: 'Dashboard',
            path: '/dashboard',
        },
        {
            name: 'View Families',
            path: '/family/viewFamily',
        },
        {
            name: familySummary,
            path: '/family/familyDetails',
            state: {
                obj,
            },
        },
    ];
    const investorRouterPath = [
        {
            name: 'Dashboard',
            path: '/investor-dashboard',
        },
        {
            name: 'View Families',
            path: '/investor-family/viewFamily',
        },
        {
            name: familySummary,
            path: '/investor-family/familyDetails',
            state: {
                obj,
            },
        },
    ];
    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BreadCrum array={isRMLikeRole(role) ? rmRouterPath : investorRouterPath} />
            </Box>
            <Box mb="25px" mt={2}>
                <Typography variant='h1' color="text.primary">{familySummary}</Typography>
            </Box>
            <Box>
                <Box
                    sx={{
                        p: { xs: "10px", sm: "25px" },
                        bgcolor: "background.paper",
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                        borderRadius: '25px',
                        minHeight: { xs: 'calc(100vh - 100px)', md: 'calc(100vh - 195px)' },
                    }}>

                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center', flexWrap: 'wrap' }}>
                        {/* Search Input */}
                        <TextField
                            sx={{
                                width: { xs: '100%', sm: 'auto' },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            sx={{ display: searchText.length > 0 ? 'inline-flex' : 'none' }}
                                            onClick={handleClearSearch} aria-label="Clear search"
                                        >
                                            <DialogCloseIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: (theme: Theme) => ({
                                    borderRadius: '10px',
                                    height: '42px',
                                    border: `1px solid ${theme.palette.text.secondary}B2`,
                                    '& fieldset': {
                                        border: 'none',
                                    },
                                    '& input': {
                                        paddingLeft: '10px',
                                        ...theme.typography.body_500,
                                        color: theme.palette.text.primary,
                                        '::placeholder': {
                                            ...theme.typography.subtitle_400,
                                            color: theme.palette.text.secondary,
                                            opacity: 1,
                                        },
                                    },
                                }),
                            }}
                            placeholder="Name, PAN and Phone"
                            value={searchText}
                            onChange={handleSearchTextChange}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '40px', mt: { xs: '20px', sm: '0px' } }}>

                            {/* Add Family */}
                            <Button sx={(theme: Theme) => ({
                                background: theme.palette.primary[500],
                                borderRadius: '4px',
                                width: '100%',
                                padding: '0.4rem',
                                gap: '5px',
                                '&:hover': {
                                    backgroundColor: theme.palette.primary[500]
                                }
                            })}
                                onClick={() =>
                                    history.push("/family/addFamilyMember", {
                                        obj,
                                    })
                                }
                            >
                                <PlusIcon />
                                <Typography variant="body_500" sx={(theme: Theme) => ({
                                    color: theme.palette.textDark.primary,
                                })}>Add Family Member</Typography>
                            </Button>
                        </Box>
                    </Box>

                    {/* Family members table UI */}
                    <Box sx={{ mt: "35px" }}>
                        <CustomTable
                            isLoading={isLoading}
                            tableBody={
                                <FamilyMemberTableBody
                                    data={filteredFamilyMembers}
                                    anchorEl={anchorEl}
                                    onOpenMenu={handleOpenMenu}
                                />
                            }
                            tableData={filteredFamilyMembers}
                            tableHeaderData={familyMembersTableHeader}
                            isPaginationVisible={false}
                            tableDataCount={filteredFamilyMembers.length}
                            tableHeadFontStyles={{
                                fontSize: "12px",
                                letterSpacing: "0.02px",
                            }}
                            tableHeadRowCustomStyles={{
                                "& th": {
                                    backgroundColor: theme.palette.textDark.disabled,
                                },
                            }}
                            tableBodyCustomStyles={{
                                "& tr:last-child td": {
                                    border: 0,
                                },
                            }}
                            loadingRowsNum={5}
                        />
                    </Box>

                </Box>

                <Menu
                    id="family-menu"
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "center",
                    }}
                    sx={{
                        boxShadow: "none",
                        "& .MuiPaper-root": {
                            width: "100px",
                        },
                    }}
                >
                    <MenuItem
                        onClick={handleRemove}
                        disabled={isRemove}
                        sx={(t: Theme) => ({
                            padding: "0.3rem 0.5rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                        })}
                    >
                        {isRemove ? 'Removing...' : "Remove"}
                    </MenuItem>
                </Menu>

                <Dialog
                    open={otpDialogOpen}
                    onClose={handleCloseOtpDialog}
                    sx={(t: Theme) => ({
                        "& .MuiPaper-root": {
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            width: 530,
                            maxWidth: "100%",
                            padding: "24px",
                            borderRadius: "12px"
                        },
                    })}
                >
                    <DialogContent sx={{ width: "100%" }}>
                        <OTPForm
                            onSubmit={handleSubmitOtp}
                            onResend={handleResendOtp}
                            otpMessage={otpMessage}
                            warningMessage=""
                        />
                    </DialogContent>
                </Dialog>


            </Box>
        </>
    );
}
